import React from "react";
import { Box, Flex, Grid, GridItem, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import OrderHistoryInvoiceAccordion from "@/features/my-order/features/order-history/components/invoice-section/OrderHistoryInvoiceAccordion";
import OrderHistoryInvoiceToggleBar from "@/features/my-order/features/order-history/components/OrderHistoryInvoiceToggleBar";
import OrderHistorySubPriceInfo from "@/features/my-order/features/order-history/components/OrderHistorySubPriceInfo";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import { toCamelKeyObject } from "@/utils/case";
import { formatDateToYMDEGeneral, toLocaleKrw } from "@/shared/utils";
import { OrderHistoryGetDetailResponse } from "@/features/my-order/features/order-history/types";

type Currency = "KRW" | "EUR";

const toCurrency = (code?: string): Currency => (code === "EUR" ? "EUR" : "KRW");

/**
 * 오더 컨펌 인보이스(OC) 디파짓 송금 — 상품대금(프로포르마)과 같은 모양으로 OC 별 바이어 송금 요청과 차액을 보여준다.
 * 바이어에게는 공개된 OC 만 내용이 실려 오므로(서버) 이름이 없는 OC 는 건너뛴다.
 */
function OrderHistoryInvoiceDepositArticle() {
  const { tTitle, tCurrencyKrw, tCurrencyString, isKor } = useI18nHelper();
  const { data } = useOrderHistoryDetail();

  const payments = (data?.payments ?? [])
    .map((rawPayment) => toCamelKeyObject(rawPayment) as LightPayment)
    .filter((payment) => payment.status !== "CANCELED");

  const rows = (data?.order_confirmation_invoices ?? [])
    .filter(
      (oc) =>
        !!oc.name &&
        oc.is_public !== false &&
        oc.status !== "CANCELED" &&
        oc.status !== "REPLACED"
    )
    .map((oc) => {
      const ocPayments = payments.filter((payment) =>
        payment.orderConfirmationInvoices?.some((linked) => linked.id === oc.id)
      );
      // 바이어가 보내는 디파짓 — 에이전시 또는 부티크(3자무역사)로 보낸다
      const depositPayment = ocPayments.find(
        (payment) => payment.type !== "ADJUSTMENT" && payment.sender?.type === "BUYER"
      );
      // 차액 — 바이어가 더 보내면 +, 에이전시가 돌려주면 -
      const adjustments = ocPayments
        .filter((payment) => payment.type === "ADJUSTMENT")
        .map((payment) => {
          const amount =
            payment.sender?.type === "BUYER"
              ? payment.amount.value
              : -payment.amount.value;
          const status = payment.isRemitted
            ? amount > 0
              ? "REMITTED"
              : "RETURNED"
            : amount > 0
            ? "REMITTANCE_REQUIRED"
            : "RETURN_REQUIRED";
          return { payment, amount, status } as const;
        });
      return { oc, depositPayment, adjustments };
    })
    .filter((row) => !!row.depositPayment);

  if (rows.length === 0) {
    return null;
  }

  // 머리말 합계 — 원화·유로(바이어 유로 송금)를 따로 더한다
  const totalByCurrency = { KRW: 0, EUR: 0 };
  rows.forEach(({ depositPayment, adjustments }) => {
    const currency = toCurrency(depositPayment!.amount.currency);
    totalByCurrency[currency] +=
      depositPayment!.amount.value +
      adjustments.reduce((acc, adjustment) => acc + adjustment.amount, 0);
  });
  const totalLabel = [
    totalByCurrency.KRW || !totalByCurrency.EUR ? tCurrencyKrw(totalByCurrency.KRW) : null,
    totalByCurrency.EUR
      ? tCurrencyString(totalByCurrency.EUR, { type: "SYMBOL", code: "EUR" })
      : null,
  ]
    .filter(Boolean)
    .join(" + ");

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex gap={"4px"}>
        <CustomIcon src={"package"} width={24} height={24} color={"#8F8F8C"} />
        <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
          {`${tTitle("deposit")} (${totalLabel})`}
        </Text>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        {rows.map(({ oc, depositPayment, adjustments }, index) => {
          const payment = depositPayment!;
          const currency = toCurrency(payment.amount.currency);
          const totalAmount =
            payment.amount.value +
            adjustments.reduce((acc, adjustment) => acc + adjustment.amount, 0);
          return (
            <OrderHistoryInvoiceToggleBar
              key={oc.id}
              index={index}
              name={oc.name}
              amount={totalAmount}
              currency={currency}
              defaultOpen={!payment.isRemitted}
            >
              {({ isOpen }) => (
                <Flex flexDirection={"column"} gap={"8px"}>
                  <OrderHistoryInvoiceAccordion
                    isOpen={isOpen}
                    btnComponent={
                      <OrderHistorySubPriceInfo
                        variant={"ORDER_CONFIRMATION"}
                        amount={payment.amount.value}
                        currency={currency}
                        status={payment.isRemitted ? "REMITTED" : "REMITTANCE_REQUIRED"}
                      />
                    }
                  >
                    <DepositDetailGrid
                      oc={oc}
                      collectedAmount={payment.amount.value}
                      remittedOn={payment.remittedOn as unknown as string | null}
                    />
                  </OrderHistoryInvoiceAccordion>
                  {adjustments.map(({ payment: adjustmentPayment, amount, status }) => (
                    <OrderHistoryInvoiceAccordion
                      key={adjustmentPayment.id}
                      isOpen={isOpen}
                      btnComponent={
                        <OrderHistorySubPriceInfo
                          variant={"DEPOSIT_DIFFERENCE"}
                          amount={amount}
                          currency={toCurrency(adjustmentPayment.amount.currency)}
                          status={status}
                        />
                      }
                    >
                      {adjustmentPayment.remittedOn && (
                        <Box padding={"8px 0px 12px 44px"}>
                          <Box
                            padding={"16px"}
                            borderRadius={"8px"}
                            boxShadow={"0 0 0 0.5px #D4D4D1"}
                          >
                            <UnorderedList textStyle={"caption-regular"} color={"#6F6F6C"}>
                              <ListItem>
                                {tTitle(
                                  `order_history:invoice.${
                                    status === "RETURNED" ? "returned_date" : "remitted_date"
                                  }`
                                )}{" "}
                                :{" "}
                                {formatDateToYMDEGeneral(
                                  adjustmentPayment.remittedOn as unknown as string,
                                  isKor,
                                  "-"
                                )}
                              </ListItem>
                            </UnorderedList>
                          </Box>
                        </Box>
                      )}
                    </OrderHistoryInvoiceAccordion>
                  ))}
                </Flex>
              )}
            </OrderHistoryInvoiceToggleBar>
          );
        })}
      </Flex>
    </Flex>
  );
}

export default OrderHistoryInvoiceDepositArticle;

type DepositOc = NonNullable<
  OrderHistoryGetDetailResponse["order_confirmation_invoices"]
>[number];

interface DepositDetailGridProps {
  oc: DepositOc;
  // 바이어 송금 요청 금액(청구) — 아래 환율·은행 수수료로 계산된 송금 필요 원화 금액
  collectedAmount: number;
  remittedOn: string | null;
}

/**
 * 디파짓 원화 환산 내역 — 프로포르마와 같이 청구 금액과 실 송금액을 나눠 보여준다.
 * 청구 금액은 OC 환율·은행 수수료(바이어 송금 요청을 계산한 값, 선송금이면 실제 송금 값으로 바뀐다),
 * 실 송금액은 3자무역사·부티크로 송금한 실제 환율·은행 수수료다 — 둘의 차이가 디파짓 차액이다.
 */
const DepositDetailGrid = ({ oc, collectedAmount, remittedOn }: DepositDetailGridProps) => {
  const { tTitle, tCurrencyString, isKor } = useI18nHelper();
  const deposit = oc.total_required_amount?.value ?? 0;
  const exchangeRate = oc.exchange_rate ?? 0;
  const bankFee = oc.bank_fee ?? 0;
  const isRemitted = !!oc.is_remitted_to_boutique && oc.actual_exchange_rate != null;
  const actualExchangeRate = oc.actual_exchange_rate ?? 0;
  const actualBankFee = oc.actual_bank_fee ?? 0;
  const actualTotalKrwAmount = deposit * actualExchangeRate;
  const money = { type: "SYMBOL" as const, code: "EUR" as const };

  const rows: { label: string; requested: string; actual: string }[] = [
    {
      label: tTitle("order_history:invoice.deposit_amount"),
      requested: tCurrencyString(deposit, money),
      actual: isRemitted ? tCurrencyString(deposit, money) : "-",
    },
    {
      label: tTitle("common:exchange_rate"),
      requested: toLocaleKrw(exchangeRate, 2, 2),
      actual: isRemitted ? toLocaleKrw(actualExchangeRate, 2, 2) : "-",
    },
    {
      label: tTitle("order_history:invoice.total_krw_amount"),
      requested: toLocaleKrw(deposit * exchangeRate),
      actual: isRemitted ? toLocaleKrw(actualTotalKrwAmount) : "-",
    },
    {
      label: tTitle("common:bank_fee"),
      requested: toLocaleKrw(bankFee),
      actual: isRemitted ? toLocaleKrw(actualBankFee) : "-",
    },
  ];

  return (
    <Box padding={"8px 0px 12px 44px"}>
      <Grid
        templateColumns={"repeat(3, 1fr)"}
        padding={"16px"}
        borderRadius={"8px"}
        boxShadow={"0 0 0 0.5px #D4D4D1"}
        sx={{
          "& > *:nth-of-type(3n + 2), & > *:nth-of-type(3n + 3)": { textAlign: "end" },
          "& > *:nth-of-type(3n + 1)": { paddingLeft: "8px" },
          "& > *:nth-of-type(3n + 3)": { paddingRight: "8px" },
        }}
        alignItems={"center"}
      >
        <GridItem textStyle={"body-sm-medium"} color={"#444444"} paddingBottom={"2px"}>
          {tTitle("order_history:invoice.category")}
        </GridItem>
        <GridItem textStyle={"body-sm-medium"} color={"#444444"} paddingBottom={"2px"}>
          {tTitle("order_history:invoice.requested_amount")}
        </GridItem>
        <GridItem textStyle={"body-sm-medium"} color={"#444444"} paddingBottom={"2px"}>
          {tTitle("order_history:invoice.remitted_amount")}
        </GridItem>

        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <GridItem textStyle={"body-sm-regular"} color={"#6F6F6C"} paddingY={"2px"}>
              {row.label}
            </GridItem>
            <GridItem textStyle={"body-sm-regular"} color={"#6F6F6C"} paddingY={"2px"}>
              {row.requested}
            </GridItem>
            <GridItem textStyle={"body-sm-regular"} color={"#6F6F6C"} paddingY={"2px"}>
              {row.actual}
            </GridItem>
          </React.Fragment>
        ))}

        <GridItem
          textStyle={"body-sm-medium"}
          color={"#444444"}
          paddingY={"10px"}
          background={"#FFF7CC"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
          marginTop={"4px"}
        >
          {tTitle("order_history:invoice.total_collected_krw_amount")}
        </GridItem>
        <GridItem
          paddingY={"10px"}
          background={"#FFF7CC"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
          textStyle={"body-md-semi-bold"}
          color={remittedOn ? "#444444" : "#CC1D1D"}
          marginTop={"4px"}
          lineHeight={"20px"}
        >
          {toLocaleKrw(collectedAmount)}
        </GridItem>
        <GridItem
          paddingY={"10px"}
          background={"#FFF7CC"}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
          textStyle={"body-md-semi-bold"}
          color={"#1272EF"}
          marginTop={"4px"}
          lineHeight={"20px"}
        >
          {/* 서버 차액 계산과 같이 원 미만 절사(은행 출금액 기준) */}
          {isRemitted ? toLocaleKrw(Math.floor(actualTotalKrwAmount + actualBankFee)) : "-"}
        </GridItem>

        <GridItem paddingTop={"12px"}>
          <UnorderedList textStyle={"caption-regular"} color={"#6F6F6C"}>
            <ListItem>
              {tTitle("order_history:invoice.issued_date")} :{" "}
              {formatDateToYMDEGeneral(oc.issued_on as string, isKor, "-")}
            </ListItem>
            {remittedOn && (
              <ListItem>
                {tTitle("order_history:invoice.remitted_date")} :{" "}
                {formatDateToYMDEGeneral(remittedOn, isKor, "-")}
              </ListItem>
            )}
          </UnorderedList>
        </GridItem>
      </Grid>
    </Box>
  );
};
